import { DeviceType, IGroup, IRegistrationInfo, ISubscriber, IUser } from '@/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useTeamStore } from '@/store/team.store';
import axios from 'axios';
import { useNotificationsStore } from './notifications.store';
import { useSubscribersStore } from './subscribers.store';
import { getAuth, signOut, User } from "firebase/auth"

const apiBase = import.meta.env.VITE_API_URL;

export type RetailRegistrationPayload = {
  name: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
};

export const useAuthStore = defineStore('authStore', () => {
  const teamStore = useTeamStore();
  const subscribersStore = useSubscribersStore();
  const currectFirebaseUser = ref<User | null>(null);
  const firebaseAuth = getAuth();
  const notificationsStore = useNotificationsStore();
  const uid = ref<string>('-');
  const idAuthToken = ref<string>('');

  const user = computed<IUser>(() => teamStore.usersMap.get(uid.value) || {
    id: uid.value,
    email: '',
    permissions: {}
  });

  const subscriber = computed<ISubscriber>(() => subscribersStore.subscribersMap.get(uid.value) || {
    id: uid.value,
    email: '',
    address: {},
    delivery_address: {},
    notifications: [],
    created: 0,
  });

  const group = computed<IGroup | undefined>(() => (user.value.group && teamStore.groupsMap.get(user.value.group)) || undefined);

  const isAuthed = computed<boolean | null>(() => user.value.id === '-' ? null : !!user.value.id);

  const fetchIdToken = (force?: boolean): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (currectFirebaseUser.value) {
        currectFirebaseUser.value
          .getIdToken(force)
          .then((token) => {
            idAuthToken.value = token;
            resolve();
          })
          .catch(() => {
            if (currectFirebaseUser.value) {
              currectFirebaseUser.value
                .getIdToken(force)
                .then((token) => {
                  idAuthToken.value = token;
                  resolve();
                })
                .catch((err) => {
                  console.error(err);
                  reject(err);
                });
            }
          });
      }
    });
  };

  firebaseAuth.onIdTokenChanged((firebaseUser) => {
    currectFirebaseUser.value = firebaseUser;
    fetchIdToken();
  });

  
  firebaseAuth.onAuthStateChanged((firebaseUser: User | null) => {
    if (firebaseUser) {
      fetchIdToken()
        .then(() => {
          let fetchedUser:IUser;
          Promise.all([
            teamStore.fetchUser(firebaseUser.uid)
            .then((_user) => {
              fetchedUser = _user;
            })
          ])
          .then(() => {
            if (!fetchedUser.role) {
              subscribersStore.fetchSubscriber(firebaseUser.uid)
                .then(() => {
                  uid.value = firebaseUser.uid;
                })
                .catch((err) => {
                  uid.value = '';
                  console.error(err);
                })
            } else if (fetchedUser.group) {
              teamStore.fetchGroup(fetchedUser.group)
                .then(() => {
                  uid.value = firebaseUser.uid;
                })
                .catch((err) => {
                  uid.value = '';
                  console.error(err);
                })
            } else {
              uid.value = firebaseUser.uid;
            }
          })
          .catch((err) => {
            uid.value = '';
            console.error(err);
          });
        })
        .catch((err) => {
          uid.value = '';
          console.error(err);
        });
      
      
    } else {
      uid.value = '';
    }
  });

  const register = (registrationInfo: IRegistrationInfo):Promise<{ orderId: number, deviceType: DeviceType }> => {
    return new Promise((resolve, reject) => {
      axios<{ orderId: number, deviceType: DeviceType }>({
        method: 'POST',
        url: apiBase + `/subscribers`,
        headers: {
          Authorization: 'Bearer ' + idAuthToken.value,
        },
        data: registrationInfo,
      })
        .then((result) =>  {
          resolve(result.data);
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Registration Failed');
          reject(err);
        });
    });
  };

  const canRegister = (registrationInfo: IRegistrationInfo):Promise<boolean> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/subscribers/can-register`,
        headers: {
          Authorization: 'Bearer ' + idAuthToken.value,
        },
        data: registrationInfo,
      })
        .then(() =>  {
          resolve(true);
        })
        .catch((err) => {
          const details = err?.response?.data?.details;
          if (err?.response?.data === 'taken') {
            notificationsStore.error('Email Already Registered');
            reject('taken');
          } else if (details === 'not-in-table') {
            notificationsStore.error('Your information was not found in our customer list. Please contact your insurance provider.');
            reject('not-in-table');
          } else {
            reject(err);
          }
        });
    });
  };

  const canRegisterRetail = (payload: RetailRegistrationPayload): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/subscribers/can-register-retail`,
        headers: {
          Authorization: 'Bearer ' + idAuthToken.value,
        },
        data: payload,
      })
        .then(() => resolve(true))
        .catch((err) => {
          if (err?.response?.data === 'taken') {
            reject('taken');
            return;
          }
          if (err?.response?.data?.details === 'on-insurance-list') {
            reject({
              type: 'on-insurance-list',
              linkId: err?.response?.data?.linkId,
            });
            return;
          }
          reject(err);
        });
    });
  };

  const registerRetail = (payload: RetailRegistrationPayload): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/subscribers/retail`,
        headers: {
          Authorization: 'Bearer ' + idAuthToken.value,
        },
        data: payload,
      })
        .then(() => resolve())
        .catch((err) => {
          if (err?.response?.data === 'taken') {
            reject('taken');
            return;
          }
          if (err?.response?.data?.details === 'on-insurance-list') {
            reject({
              type: 'on-insurance-list',
              linkId: err?.response?.data?.linkId,
            });
            return;
          }
          notificationsStore.error('Registration Failed');
          reject(err);
        });
    });
  };

  const logOut = ():void => {
    signOut(firebaseAuth);
  };

  const companiesVisible = computed<boolean>(() => {
    return !!user.value.permissions?.admin && !user.value.group;
  });

  const teamManagenetVisible = computed<boolean>(() => {
    return !user.value.permissions?.admin;
  });

  const isSubscriber = computed<boolean>(() => {
    return isAuthed.value === true && !user.value.role;
  });

  const isUser = computed<boolean>(() => {
    return isAuthed.value === true && !!user.value.role;
  });

  const isRetailSubscriber = computed<boolean>(() => {
    return isSubscriber.value && (subscriber.value.customerType === 'retail');
  });

  const isInsuranceSubscriber = computed<boolean>(() => {
    return isSubscriber.value && subscriber.value.customerType !== 'retail';
  });

  return {
    user,
    group,
    isAuthed,
    companiesVisible,
    logOut,
    teamManagenetVisible,
    isSubscriber,
    isUser,
    isRetailSubscriber,
    isInsuranceSubscriber,
    register,
    registerRetail,
    idAuthToken,
    subscriber,
    canRegister,
    canRegisterRetail,
  };
});